// Chemistry utilities for molecule parsing and generation
import { Molecule, Atom, Bond, BondType, ELEMENTS } from '../types/molecule';
import { EXAMPLE_MOLECULES } from './exampleMolecules';

// Generate a unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Parse molecular formula
function parseFormula(atoms: Atom[]): string {
  const counts: Record<string, number> = {};
  for (const atom of atoms) {
    counts[atom.element] = (counts[atom.element] || 0) + 1;
  }
  
  // Sort by atomic number (C first, then H, then others alphabetically)
  const order = ['C', 'H'];
  const others = Object.keys(counts).filter(e => !order.includes(e)).sort();
  
  let formula = '';
  if (counts['C']) {
    formula += 'C' + (counts['C'] > 1 ? counts['C'] : '');
    delete counts['C'];
  }
  if (counts['H']) {
    formula += 'H' + (counts['H'] > 1 ? counts['H'] : '');
    delete counts['H'];
  }
  for (const element of others) {
    formula += element + (counts[element] > 1 ? counts[element] : '');
  }
  
  return formula;
}

// Calculate molecular weight
function calculateMolecularWeight(atoms: Atom[]): number {
  let weight = 0;
  for (const atom of atoms) {
    const element = ELEMENTS[atom.element];
    weight += element?.atomicMass || 12;
  }
  return weight;
}

// Parse SMILES string and generate 3D coordinates (simplified algorithm)
export async function parseSMILES(smiles: string, name?: string): Promise<Molecule | null> {
  if (!smiles || !smiles.trim()) return null;

  // Try to find in examples first
  const example = EXAMPLE_MOLECULES.find(
    m => m.name.toLowerCase() === smiles.toLowerCase() ||
         m.smiles.toLowerCase() === smiles.toLowerCase()
  );

  if (example) {
    smiles = example.smiles;
    name = example.name;
  }

  // Simplified SMILES parser - generates pseudo-3D coordinates
  const atoms: Atom[] = [];
  const bonds: Bond[] = [];
  
  // Stack for ring closure tracking
  const ringAtoms: number[] = [];
  const ringNumbers: Record<string, number> = {};
  
  // Current position in 3D space
  let x = 0, y = 0, z = 0;
  let currentAtomIndex = 0;
  
  // Parse SMILES string character by character
  const chars = smiles.replace(/\s/g, '').split('');
  let i = 0;
  
  while (i < chars.length) {
    const char = chars[i];
    
    // Handle aromatic lowercase letters
    if (char === char.toLowerCase() && /[a-z]/.test(char)) {
      // Convert aromatic to uppercase
      const upperChar = char.toUpperCase();
      if (ELEMENTS[upperChar]) {
        atoms.push({
          id: generateId(),
          element: upperChar,
          x, y, z,
          formalCharge: 0,
          implicitHydrogens: 0,
          isotope: 0,
          aromatic: true,
          chiral: false,
        });
      }
      i++;
      continue;
    }
    
    // Handle ring numbers
    if (/\d/.test(char)) {
      let numStr = char;
      i++;
      while (i < chars.length && /\d/.test(chars[i])) {
        numStr += chars[i];
        i++;
      }
      
      if (ringNumbers[numStr] !== undefined) {
        // Ring closure
        const otherAtomIndex = ringNumbers[numStr];
        const bondType = determineBondType(atoms[currentAtomIndex - 1], atoms[otherAtomIndex]);
        bonds.push({
          id: generateId(),
          atomIndex1: otherAtomIndex,
          atomIndex2: currentAtomIndex - 1,
          type: bondType,
          order: getBondOrder(bondType),
        });
        delete ringNumbers[numStr];
      } else {
        // Mark atom for ring closure
        ringNumbers[numStr] = currentAtomIndex - 1;
      }
      continue;
    }
    
    // Handle branch opening
    if (char === '(') {
      i++;
      continue;
    }
    
    // Handle branch closing
    if (char === ')') {
      i++;
      continue;
    }
    
    // Handle double bond
    if (char === '=') {
      i++;
      continue;
    }
    
    // Handle triple bond
    if (char === '#') {
      i++;
      continue;
    }
    
    // Handle aromatic bond
    if (char === ':') {
      i++;
      continue;
    }
    
    // Handle implicit hydrogen number
    if (char === 'H') {
      i++;
      let hNumStr = '';
      while (i < chars.length && /\d/.test(chars[i])) {
        hNumStr += chars[i];
        i++;
      }
      if (currentAtomIndex > 0) {
        const lastAtom = atoms[currentAtomIndex - 1];
        lastAtom.implicitHydrogens = hNumStr ? parseInt(hNumStr) : 1;
      }
      continue;
    }
    
    // Handle charge
    if (char === '+' || char === '-') {
      i++;
      continue;
    }
    
    // Handle element symbols
    if (ELEMENTS[char]) {
      atoms.push({
        id: generateId(),
        element: char,
        x, y, z,
        formalCharge: 0,
        implicitHydrogens: 0,
        isotope: 0,
        aromatic: false,
        chiral: false,
      });
      
      // Add bond to previous atom
      if (currentAtomIndex > 0) {
        const prevAtom = atoms[currentAtomIndex - 1];
        const bondType = determineBondType(prevAtom, atoms[currentAtomIndex]);
        bonds.push({
          id: generateId(),
          atomIndex1: currentAtomIndex - 1,
          atomIndex2: currentAtomIndex,
          type: bondType,
          order: getBondOrder(bondType),
        });
      }
      
      // Move to next position in 3D space
      const angle = (currentAtomIndex * 137.5) * (Math.PI / 180); // Golden angle
      const radius = 1.5 + (currentAtomIndex % 3) * 0.5;
      x = Math.cos(angle) * radius * ((currentAtomIndex % 2) ? 1 : -1);
      y = (currentAtomIndex % 5) * 0.5 - 1;
      z = Math.sin(angle) * radius;
      
      currentAtomIndex++;
      i++;
      continue;
    }
    
    // Handle two-letter elements
    if (i + 1 < chars.length) {
      const twoChar = char + chars[i + 1];
      if (ELEMENTS[twoChar]) {
        atoms.push({
          id: generateId(),
          element: twoChar,
          x, y, z,
          formalCharge: 0,
          implicitHydrogens: 0,
          isotope: 0,
          aromatic: false,
          chiral: false,
        });
        
        if (currentAtomIndex > 0) {
          const prevAtom = atoms[currentAtomIndex - 1];
          const bondType = determineBondType(prevAtom, atoms[currentAtomIndex]);
          bonds.push({
            id: generateId(),
            atomIndex1: currentAtomIndex - 1,
            atomIndex2: currentAtomIndex,
            type: bondType,
            order: getBondOrder(bondType),
          });
        }
        
        const angle = (currentAtomIndex * 137.5) * (Math.PI / 180);
        const radius = 1.5 + (currentAtomIndex % 3) * 0.5;
        x = Math.cos(angle) * radius * ((currentAtomIndex % 2) ? 1 : -1);
        y = (currentAtomIndex % 5) * 0.5 - 1;
        z = Math.sin(angle) * radius;
        
        currentAtomIndex++;
        i += 2;
        continue;
      }
    }
    
    i++;
  }
  
  // Add implicit hydrogens to heavy atoms
  const heavyAtomCount = atoms.length;
  for (let i = 0; i < heavyAtomCount; i++) {
    const atom = atoms[i];
    if (atom.element === 'C') {
      const bondsToC = bonds.filter(b => b.atomIndex1 === i || b.atomIndex2 === i).length;
      atom.implicitHydrogens = Math.max(0, 4 - bondsToC);
    } else if (atom.element === 'N') {
      const bondsToN = bonds.filter(b => b.atomIndex1 === i || b.atomIndex2 === i).length;
      atom.implicitHydrogens = Math.max(0, 3 - bondsToN);
    } else if (atom.element === 'O') {
      const bondsToO = bonds.filter(b => b.atomIndex1 === i || b.atomIndex2 === i).length;
      atom.implicitHydrogens = Math.max(0, 2 - bondsToO);
    } else if (atom.element === 'S') {
      const bondsToS = bonds.filter(b => b.atomIndex1 === i || b.atomIndex2 === i).length;
      atom.implicitHydrogens = Math.max(0, 2 - bondsToS);
    }
    
    // Add hydrogen atoms
    for (let h = 0; h < atom.implicitHydrogens; h++) {
      const hAngle = (h * 120 + i * 45) * (Math.PI / 180);
      const hRadius = 1.0;
      atoms.push({
        id: generateId(),
        element: 'H',
        x: atom.x + Math.cos(hAngle) * hRadius,
        y: atom.y + Math.sin(hAngle) * hRadius,
        z: atom.z + (h - atom.implicitHydrogens / 2) * 0.5,
        formalCharge: 0,
        implicitHydrogens: 0,
        isotope: 0,
        aromatic: false,
        chiral: false,
      });
    }
  }
  
  // If no atoms parsed, try simple elements
  if (atoms.length === 0) {
    if (ELEMENTS[smiles]) {
      atoms.push({
        id: generateId(),
        element: smiles,
        x: 0, y: 0, z: 0,
        formalCharge: 0,
        implicitHydrogens: 0,
        isotope: 0,
        aromatic: false,
        chiral: false,
      });
    } else {
      return null;
    }
  }
  
  const formula = parseFormula(atoms);
  const molecularWeight = calculateMolecularWeight(atoms);
  
  return {
    id: generateId(),
    name: name || `Molecule ${Date.now()}`,
    formula,
    atoms,
    bonds,
    molecularWeight,
    canonicalSMILES: smiles,
    charge: 0,
    spinMultiplicity: 1,
    createdAt: Date.now(),
    source: 'smiles',
  };
}

function determineBondType(atom1: Atom, atom2: Atom): BondType {
  // Simplified bond type determination
  if (atom1.aromatic || atom2.aromatic) {
    return BondType.AROMATIC;
  }
  return BondType.SINGLE;
}

function getBondOrder(bondType: BondType): number {
  switch (bondType) {
    case BondType.SINGLE: return 1;
    case BondType.DOUBLE: return 2;
    case BondType.TRIPLE: return 3;
    case BondType.AROMATIC: return 1.5;
    default: return 1;
  }
}

// Convert molecule to PDB format
export function toPDB(molecule: Molecule): string {
  let pdb = `COMPND    ${molecule.name}\n`;
  pdb += `AUTHOR    Generated by Molecule Studio AI\n`;
  pdb += `REMARK    SMILES: ${molecule.canonicalSMILES}\n`;
  pdb += `REMARK    Molecular Weight: ${molecule.molecularWeight.toFixed(3)}\n`;
  
  for (const atom of molecule.atoms) {
    const element = atom.element.padEnd(2, ' ').substring(0, 2);
    const serial = molecule.atoms.indexOf(atom) + 1;
    const x = atom.x.toFixed(3).padStart(8, ' ');
    const y = atom.y.toFixed(3).padStart(8, ' ');
    const z = atom.z.toFixed(3).padStart(8, ' ');
    const occ = '1.00';
    const temp = '0.00';
    pdb += `ATOM  ${serial.toString().padStart(5, ' ')}  ${atom.element.substring(0, 1).padEnd(3, ' ')} ${'RES'.substring(0, 3)} ${'A'.padEnd(1, ' ')}${serial.toString().padStart(4, ' ')}   ${x}${y}${z}${occ}${temp}          ${atom.element.substring(0, 1).padEnd(2, ' ')}\n`;
  }
  pdb += 'END\n';
  
  return pdb;
}

// Convert molecule to MOL/SDF format
export function toMOL(molecule: Molecule): string {
  let mol = `${molecule.name}\n`;
  mol += `Molecule Studio AI\n`;
  mol += `${new Date().toISOString().split('T')[0]}\n`;
  
  // Header block
  const nAtoms = molecule.atoms.length;
  const nBonds = molecule.bonds.length;
  mol += `${nAtoms.toString().padStart(3, ' ')}${nBonds.toString().padStart(3, ' ')}  0  0  0  0  0  0  0  0999 V2000\n`;
  
  // Atom block
  for (const atom of molecule.atoms) {
    const x = atom.x.toFixed(4).padStart(10, ' ');
    const y = atom.y.toFixed(4).padStart(10, ' ');
    const z = atom.z.toFixed(4).padStart(10, ' ');
    const symbol = atom.element.padEnd(3, ' ').substring(0, 3);
    mol += `${x}${y}${z} ${symbol}  0${atom.formalCharge > 0 ? atom.formalCharge : 0}  0  0  0  0  0  0  0  0  0  0\n`;
  }
  
  // Bond block
  for (const bond of molecule.bonds) {
    const idx1 = (bond.atomIndex1 + 1).toString().padStart(3, ' ');
    const idx2 = (bond.atomIndex2 + 1).toString().padStart(3, ' ');
    const type = bond.order === 2 ? '2' : bond.order === 3 ? '3' : '1';
    mol += `${idx1}${idx2}  ${type}  0  0  0  0\n`;
  }
  
  mol += 'M  END\n';
  
  return mol;
}

// Calculate topological polar surface area (TPSA)
export function calculateTPSA(molecule: Molecule): number {
  const contributions: Record<string, number> = {
    O: 12.28,
    N: 15.79,
    S: 25.45,
    P: 13.29,
    F: 14.71,
    Cl: 20.16,
    Br: 26.02,
    I: 32.72,
  };
  
  let tpsa = 0;
  for (const atom of molecule.atoms) {
    const contrib = contributions[atom.element];
    if (contrib) {
      tpsa += contrib;
    }
  }
  
  return tpsa;
}

// Calculate LogP (simplified)
export function calculateLogP(molecule: Molecule): number {
  const contributions: Record<string, number> = {
    C: 0.54,
    H: 0.23,
    O: -1.0,
    N: -1.0,
    S: -0.43,
    F: 0.37,
    Cl: 0.71,
    Br: 0.86,
    I: 1.05,
  };
  
  let logp = 0;
  for (const atom of molecule.atoms) {
    const contrib = contributions[atom.element];
    if (contrib) {
      logp += contrib;
    }
  }
  
  // Adjust for ring systems
  const ringCount = countRings(molecule);
  logp -= ringCount * 0.5;
  
  return logp;
}

function countRings(molecule: Molecule): number {
  // Simplified ring counting
  const bonds = molecule.bonds;
  const n = molecule.atoms.length;
  const visited = new Set<number>();
  let ringCount = 0;
  
  function dfs(node: number, start: number, depth: number): boolean {
    if (depth > n) return false;
    if (node === start && depth > 2) return true;
    
    visited.add(node);
    
    for (const bond of bonds) {
      const neighbor = bond.atomIndex1 === node ? bond.atomIndex2 : 
                       bond.atomIndex2 === node ? bond.atomIndex1 : -1;
      if (neighbor === -1) continue;
      if (neighbor === start && depth > 2) return true;
      if (!visited.has(neighbor)) {
        if (dfs(neighbor, start, depth + 1)) return true;
      }
    }
    
    return false;
  }
  
  for (let i = 0; i < n; i++) {
    if (!visited.has(i)) {
      visited.add(i);
      if (dfs(i, i, 0)) {
        ringCount++;
      }
    }
  }
  
  return ringCount;
}

// Count rotatable bonds
export function countRotatableBonds(molecule: Molecule): number {
  const rotatable = molecule.bonds.filter(bond => {
    const atom1 = molecule.atoms[bond.atomIndex1];
    const atom2 = molecule.atoms[bond.atomIndex2];
    
    // Don't count bonds to hydrogen
    if (atom1.element === 'H' || atom2.element === 'H') return false;
    
    // Don't count terminal bonds
    if (atom1.element === 'C' && atom2.element === 'C') {
      const c1Bonds = molecule.bonds.filter(b => 
        (b.atomIndex1 === bond.atomIndex1 || b.atomIndex2 === bond.atomIndex1) &&
        (b.atomIndex1 !== bond.atomIndex2 && b.atomIndex2 !== bond.atomIndex2)
      ).length;
      const c2Bonds = molecule.bonds.filter(b => 
        (b.atomIndex1 === bond.atomIndex2 || b.atomIndex2 === bond.atomIndex2) &&
        (b.atomIndex1 !== bond.atomIndex1 && b.atomIndex2 !== bond.atomIndex1)
      ).length;
      
      if (c1Bonds <= 1 || c2Bonds <= 1) return false;
    }
    
    // Don't count double/triple bonds
    if (bond.order > 1) return false;
    
    // Don't count aromatic bonds
    if (bond.type === 4) return false;
    
    return true;
  });
  
  return rotatable.length;
}

// Count hydrogen bond donors and acceptors
export function countHBA_HBD(molecule: Molecule): { hba: number; hbd: number } {
  let hba = 0;
  let hbd = 0;
  
  for (const atom of molecule.atoms) {
    if (atom.element === 'O' || atom.element === 'N') {
      // Check if it's not positively charged
      if (atom.formalCharge <= 0) {
        hba++;
      }
    }
    
    if (atom.element === 'O' || atom.element === 'N' || atom.element === 'S') {
      // Check if it has hydrogen attached
      const atomIdNum = parseInt(atom.id, 10);
      const hasHydrogen = molecule.bonds.some(bond => {
        const neighbor = bond.atomIndex1 === atomIdNum ? bond.atomIndex2 : 
                         bond.atomIndex2 === atomIdNum ? bond.atomIndex1 : -1;
        return neighbor !== -1 && molecule.atoms[neighbor]?.element === 'H';
      });
      
      if (hasHydrogen) {
        hbd++;
      }
    }
  }
  
  return { hba, hbd };
}

